/* eslint-disable @typescript-eslint/no-explicit-any */
import { isbot } from 'isbot'
import mixpanel from 'mixpanel-browser'
import { STORAGE_KEYS } from '~constants'

export default class Mixpanel {
  private static instance?: typeof mixpanel
  public static async getInstance() {
    return this.instance ?? (await this.init())
  }

  static async init() {
    if (import.meta.env.VITE_MIXPANEL_TOKEN) {
      mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
        debug: import.meta.env.DEV,
      })
      this.instance = mixpanel
      console.debug('[Mixpanel] SDK initialized')
    } else {
      throw new Error('Mixpanel SDK: MIXPANEL_TOKEN is not set')
    }

    return this.instance
  }

  static identify(ownerId: number) {
    this.getInstance().then(instance => {
      console.log('Mixpanel identify', ownerId)
      instance.identify(ownerId.toString())
      instance.people.set({
        'Owner ID': ownerId,
        Environment: import.meta.env.MODE,
        from: 'Samsung/SmartThings',
      })
      instance.register({
        'Owner ID': ownerId,
        Environment: import.meta.env.MODE,
        from: 'Samsung/SmartThings',
      })
    })
  }

  static reset() {
    this.getInstance().then(instance => {
      instance.reset()
    })
  }

  static async setDistinctId() {
    const deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID)
    const distinctId = (await this.getInstance()).get_distinct_id()

    if (!deviceId && this.instance !== undefined) {
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, distinctId)
    }

    return distinctId
  }

  static async getDistinctId() {
    return localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || (await this.setDistinctId())
  }

  static logStartEvent = (eventName: string) => {
    this.getInstance().then(instance => instance.time_event(eventName))
  }

  static logEvent = (eventName: string, eventArgs?: any) => {
    this.getInstance().then(instance => instance.track(eventName, eventArgs))
  }

  /**
   * @see https://help.mixpanel.com/hc/en-us/articles/360001337103
   */
  static traceCampaignParams = () => {
    if (isbot(navigator.userAgent)) {
      return
    }

    const getQueryParam = (url: string, param: any) => {
      // Expects a raw URL
      // 위 첨부해둔 링크에서 가져온 코드라서 eslint-disable-next-line 처리.
      // eslint-disable-next-line no-empty-character-class
      const replacedParam = param.replace(/[[]/, '[').replace(/[]]/, ']')
      const regexS = '[?&]' + replacedParam + '=([^&#]*)'
      const regex = new RegExp(regexS)
      const results = regex.exec(url)

      if (results === null || (results && typeof results[1] !== 'string')) {
        return ''
      }
      return decodeURIComponent(results[1]).replace(/\W/gi, ' ')
    }

    const getCampaignParams = () => {
      const campaign_keywords = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
        'utm_term',
      ]
      const params: Record<string, string> = {}
      const first_params: Record<string, string> = {}
      let kw = ''
      let index

      // eslint-disable-next-line no-plusplus
      for (index = 0; index < campaign_keywords.length; ++index) {
        kw = getQueryParam(document.URL, campaign_keywords[index])

        if (kw.length) {
          params[campaign_keywords[index] + ' [last touch]'] = kw
        }
      }
      // eslint-disable-next-line no-plusplus
      for (index = 0; index < campaign_keywords.length; ++index) {
        kw = getQueryParam(document.URL, campaign_keywords[index])
        if (kw.length) {
          first_params[campaign_keywords[index] + ' [first touch]'] = kw
        }
      }

      this.getInstance().then(instance => {
        instance.people.set(params)
        instance.people.set_once(first_params)
        instance.register(params)
      })
    }

    getCampaignParams()
  }
}
